import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import LinearProgress from "@mui/material/LinearProgress";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "api/axios";
import { libelleVilleDate } from "utils/oumrahVilleDepart";

const statutLabel = { en_attente: "En attente", confirmee: "Confirmées", annulee: "Annulées" };
const statutColor = { en_attente: "#fb8c00", confirmee: "#43a047", annulee: "#e53935" };

function CarteStat({ titre, valeur, icone, couleur, sousTitre }) {
  return (
    <Card sx={{ p: 2 }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center">
        <MDBox>
          <MDTypography variant="button" color="text" fontWeight="medium">{titre}</MDTypography>
          <MDTypography variant="h4" fontWeight="bold" color={couleur}>{valeur}</MDTypography>
          {sousTitre && <MDTypography variant="caption" color="text">{sousTitre}</MDTypography>}
        </MDBox>
        <MDBox
          width="3rem" height="3rem" borderRadius="lg"
          display="flex" alignItems="center" justifyContent="center"
          sx={{ bgcolor: couleur === "success" ? "#e8f5e9" : couleur === "error" ? "#ffebee" : "#e3f2fd" }}
        >
          <Icon fontSize="medium" color={couleur}>{icone}</Icon>
        </MDBox>
      </MDBox>
    </Card>
  );
}

function OumrahDashboard() {
  const [data, setData] = useState(null);
  const fmt = (n) => parseFloat(n || 0).toLocaleString("fr-FR");

  useEffect(() => {
    api.get("/oumrah/dashboard").then((res) => setData(res.data)).catch((e) => console.error(e));
  }, []);

  if (!data) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDTypography variant="body2" color="text">Chargement du tableau de bord...</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  const f = data.financier;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>

        {/* SECTION 1 : PARTICIPANTS & DEPARTS */}
        <MDBox display="flex" alignItems="center" mb={2}>
          <MDBox sx={{ width: 4, height: 28, borderRadius: 2, bgcolor: "#1565c0", mr: 1.5 }} />
          <Icon sx={{ color: "#1565c0", mr: 1 }}>groups</Icon>
          <MDTypography variant="h5" fontWeight="bold" color="dark">Participants & départs</MDTypography>
        </MDBox>
        <Grid container spacing={3} mb={5}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, borderRadius: 4, boxShadow: "0 2px 16px rgba(0,0,0,0.06)", position: "relative", overflow: "hidden" }}>
              <MDBox sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: 4, bgcolor: "#1565c0" }} />
              <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
                <MDBox>
                  <MDTypography variant="button" fontWeight="medium" color="text" textTransform="uppercase" sx={{ letterSpacing: 0.5 }}>
                    Participants
                  </MDTypography>
                  <MDTypography variant="h3" fontWeight="bold" color="dark" lineHeight={1.1} mt={0.5}>
                    {data.total_participants}
                  </MDTypography>
                </MDBox>
                <MDBox sx={{ bgcolor: "#e3f2fd", borderRadius: "50%", width: 48, height: 48, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon sx={{ color: "#1565c0" }}>group</Icon>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
          {(data.participants_par_ville_depart || []).map((v) => (
            <Grid item xs={6} sm={6} md={3} key={v.ville_depart_id || "sans-ville"}>
              <Card sx={{ p: 2.5, borderRadius: 4, boxShadow: "0 2px 16px rgba(0,0,0,0.06)", position: "relative", overflow: "hidden" }}>
                <MDBox sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: 4, bgcolor: "#26a69a" }} />
                <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
                  <MDBox>
                    <MDTypography variant="button" fontWeight="medium" color="text" textTransform="uppercase" sx={{ letterSpacing: 0.5 }}>
                      {v.ville_depart_id ? libelleVilleDate(v.ville, v.date_depart) : v.ville}
                    </MDTypography>
                    {v.session_libelle && (
                      <MDTypography variant="caption" color="text" display="block">
                        {v.session_libelle}
                      </MDTypography>
                    )}
                    <MDTypography variant="h3" fontWeight="bold" color="dark" lineHeight={1.1} mt={0.5}>
                      {v.total}
                    </MDTypography>
                  </MDBox>
                  <MDBox sx={{ bgcolor: "#e0f2f1", borderRadius: "50%", width: 48, height: 48, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon sx={{ color: "#00897b" }}>flight_takeoff</Icon>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          ))}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, borderRadius: 4, boxShadow: "0 2px 16px rgba(0,0,0,0.06)", position: "relative", overflow: "hidden" }}>
              <MDBox sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: 4, bgcolor: "#EF9F27" }} />
              <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
                <MDBox>
                  <MDTypography variant="button" fontWeight="medium" color="text" textTransform="uppercase" sx={{ letterSpacing: 0.5 }}>
                    En liste d'attente
                  </MDTypography>
                  <MDTypography variant="h3" fontWeight="bold" color="dark" lineHeight={1.1} mt={0.5}>
                    {data.total_liste_attente || 0}
                  </MDTypography>
                </MDBox>
                <MDBox sx={{ bgcolor: "#FAEEDA", borderRadius: "50%", width: 48, height: 48, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon sx={{ color: "#BA7517" }}>hourglass_top</Icon>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* SECTION 2 : SUIVI & ALERTES */}
        <MDBox display="flex" alignItems="center" mb={2}>
          <MDBox sx={{ width: 4, height: 28, borderRadius: 2, bgcolor: "#00897b", mr: 1.5 }} />
          <Icon sx={{ color: "#00897b", mr: 1 }}>fact_check</Icon>
          <MDTypography variant="h5" fontWeight="bold" color="dark">Suivi & alertes</MDTypography>
        </MDBox>
        <Grid container spacing={3} mb={5}>
          <Grid item xs={12} lg={7}>
            <Card sx={{ borderRadius: 4, boxShadow: "0 2px 16px rgba(0,0,0,0.06)", height: "100%" }}>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                  <Icon sx={{ verticalAlign: "middle", color: "#1565c0", mr: 0.5 }}>event</Icon>
                  Sessions en cours
                </MDTypography>
                {data.sessions.map((s, i) => (
                  <MDBox key={s.id} mb={i < data.sessions.length - 1 ? 2.5 : 0}
                    pb={i < data.sessions.length - 1 ? 2.5 : 0}
                    sx={{ borderBottom: i < data.sessions.length - 1 ? "1px solid #f0f0f0" : "none" }}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <MDTypography variant="button" fontWeight="bold" color="dark">{s.libelle}</MDTypography>
                      <MDTypography variant="caption" color="text" fontWeight="medium">
                        {s.places_reservees}/{s.places_total} places · {s.nb_inscriptions} insc.
                      </MDTypography>
                    </MDBox>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, s.taux_remplissage)}
                      sx={{ height: 8, borderRadius: 4, my: 1, bgcolor: "#eef1f5",
                        "& .MuiLinearProgress-bar": { borderRadius: 4, bgcolor: s.taux_remplissage >= 90 ? "#43a047" : "#1565c0" } }}
                    />
                    <MDBox display="flex" justifyContent="space-between">
                      <MDTypography variant="caption" color="text">
                        Dû : {fmt(s.du)} € · Payé : {fmt(s.paye)} €
                      </MDTypography>
                      <MDTypography variant="caption" fontWeight="bold" color={s.reste > 0 ? "error" : "success"}>
                        Reste : {fmt(s.reste)} €
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                ))}
                {data.sessions.length === 0 && (
                  <MDTypography variant="caption" color="text">Aucune session active.</MDTypography>
                )}
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} lg={5}>
            <Card sx={{ borderRadius: 4, boxShadow: "0 2px 16px rgba(0,0,0,0.06)", mb: 3 }}>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                  <Icon sx={{ verticalAlign: "middle", color: "#5e72e4", mr: 0.5 }}>pie_chart</Icon>
                  Inscriptions par statut
                </MDTypography>
                <MDBox display="flex" gap={1} flexWrap="wrap">
                  {data.statuts.map((st) => (
                    <Chip key={st.statut}
                      label={(statutLabel[st.statut] || st.statut) + " : " + st.nb}
                      sx={{ bgcolor: statutColor[st.statut] || "#9e9e9e", color: "white", fontWeight: "bold", borderRadius: 2 }} />
                  ))}
                  {data.statuts.length === 0 && (
                    <MDTypography variant="caption" color="text">Aucune inscription.</MDTypography>
                  )}
                </MDBox>
              </MDBox>
            </Card>
            <Card sx={{ borderRadius: 4, boxShadow: "0 2px 16px rgba(0,0,0,0.06)" }}>
              <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                  <Icon sx={{ verticalAlign: "middle", color: "#fb8c00", mr: 0.5 }}>warning</Icon>
                  Passeports à vérifier
                </MDTypography>
                {data.passeports_alerte.map((p, idx) => (
                  <MDBox key={idx} display="flex" alignItems="center" py={0.75}
                    sx={{ borderBottom: idx < data.passeports_alerte.length - 1 ? "1px solid #f5f5f5" : "none" }}>
                    <MDBox sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#fb8c00", mr: 1.5, flexShrink: 0 }} />
                    <MDTypography variant="caption" color="dark">
                      <strong>{p.prenom} {p.nom}</strong> — expire le {new Date(p.date_expiration_passeport).toLocaleDateString("fr-FR")}
                      <MDTypography variant="caption" color="text" component="span"> ({p.session_libelle})</MDTypography>
                    </MDTypography>
                  </MDBox>
                ))}
                {data.passeports_alerte.length === 0 && (
                  <MDBox display="flex" alignItems="center">
                    <Icon sx={{ color: "#43a047", mr: 1 }}>check_circle</Icon>
                    <MDTypography variant="caption" color="text">Aucun passeport à signaler.</MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* SECTION 3 : FINANCES */}
        <MDBox display="flex" alignItems="center" mb={2}>
          <MDBox sx={{ width: 4, height: 28, borderRadius: 2, bgcolor: "#fb8c00", mr: 1.5 }} />
          <Icon sx={{ color: "#fb8c00", mr: 1 }}>account_balance_wallet</Icon>
          <MDTypography variant="h5" fontWeight="bold" color="dark">Finances</MDTypography>
        </MDBox>
        <Grid container spacing={3} mb={3}>
          {[
            { titre: "Total dû", valeur: fmt(f.total_du) + " €", icone: "account_balance", accent: "#1565c0", bg: "#e3f2fd" },
            { titre: "Encaissé", valeur: fmt(f.total_paye) + " €", icone: "payments", accent: "#43a047", bg: "#e8f5e9" },
            { titre: "Reste à encaisser", valeur: fmt(f.reste) + " €", icone: "hourglass_bottom", accent: "#e53935", bg: "#ffebee" },
            { titre: "Recouvrement", valeur: f.taux_recouvrement + " %", icone: "trending_up", accent: "#fb8c00", bg: "#fff3e0" },
          ].map((c) => (
            <Grid item xs={12} sm={6} lg={3} key={c.titre}>
              <Card sx={{ p: 2.5, borderRadius: 4, boxShadow: "0 2px 16px rgba(0,0,0,0.06)", position: "relative", overflow: "hidden" }}>
                <MDBox sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: 4, bgcolor: c.accent }} />
                <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
                  <MDBox>
                    <MDTypography variant="button" fontWeight="medium" color="text" textTransform="uppercase" sx={{ letterSpacing: 0.5 }}>
                      {c.titre}
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold" color="dark" lineHeight={1.1} mt={0.5}>
                      {c.valeur}
                    </MDTypography>
                  </MDBox>
                  <MDBox sx={{ bgcolor: c.bg, borderRadius: "50%", width: 48, height: 48, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon sx={{ color: c.accent }}>{c.icone}</Icon>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Card sx={{ borderRadius: 4, boxShadow: "0 2px 16px rgba(0,0,0,0.06)" }}>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
              <Icon sx={{ verticalAlign: "middle", color: "#e53935", mr: 0.5 }}>euro</Icon>
              À relancer (reste à payer)
            </MDTypography>
            {data.relances.map((r, idx) => (
              <MDBox key={idx} display="flex" justifyContent="space-between" alignItems="center" py={1}
                sx={{ borderBottom: idx < data.relances.length - 1 ? "1px solid #f5f5f5" : "none" }}>
                <MDTypography variant="body2" color="text">
                  <strong>{r.directeur_nom}</strong>{r.directeur_ville ? " (" + r.directeur_ville + ")" : ""} · {r.session_libelle}
                </MDTypography>
                <MDTypography variant="body2" fontWeight="bold" color="error">{fmt(r.reste)} €</MDTypography>
              </MDBox>
            ))}
            {data.relances.length === 0 && (
              <MDBox display="flex" alignItems="center">
                <Icon sx={{ color: "#43a047", mr: 1 }}>check_circle</Icon>
                <MDTypography variant="caption" color="text">Tout est soldé.</MDTypography>
              </MDBox>
            )}
          </MDBox>
        </Card>

      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
export default OumrahDashboard;
